import { buildStudioCartTiles } from "~/shared/studioCartTiles";
import { getShapeById } from "~/composables/shapes/utils";
/**
 * Extracted body of the Pinia `project` store's `addToCart` action.
 *
 * Moved out of `stores/project.ts` to keep that file under the `.ts` size cap.
 * Behavior is preserved — `store` is the Pinia store instance (`this` in the
 * action). Recursive calls into `store.addToCart(...)` go back through the
 * thin wrapper in the store, which re-enters here.
 */
import { storeToRefs } from "pinia";
import { nextTick } from "vue";
import { useNuxtApp, useState } from "#app";
import { GRID_PATTERN_IDS } from "~/composables/grid/constants";
import { useGridStore } from "~/stores/grid";
import { useLoadingStore } from "~/stores/loading";
import { ModalType, useModalStore } from "~/stores/modal";
import { useUserStore } from "~/stores/user";
import type { ShapeInterface } from "~/types/grid.types";
import { submitCartAddViaForm } from "~/composables/submitCartAddForm";

export interface AddToCartOptions {
  bypassLogin?: boolean;
  bypassDesignName?: boolean;
  bypassSaveDesign?: boolean;
  attachmentOption?: "adhesive" | "screws" | null;
  /**
   * FR-421: when true, asks the server to skip the consolidated/generic cart
   * flow and use the legacy per-design Shopify-product path. The server
   * authorizes this override against `@feltright.com` email; a non-team
   * request with the flag is ignored server-side, not errored.
   */
  forceLegacyFlow?: boolean;
}

export const CART_IMAGE_PROCESSING_MESSAGE =
  "Your design preview is still processing. Please try Add to Cart again.";
export const CART_IMAGE_REJECTED_MESSAGE =
  "Your design preview could not be created. Please try Add to Cart again. If the problem persists, please contact support.";

export const DEFAULT_CART_ERROR_MESSAGE =
  "Failed to create Shopify product. Please, try again. If the problem persists, please contact support.";

export function getAddToCartErrorMessage(error: unknown): string {
  const candidate = error as {
    data?: {
      code?: unknown;
      data?: { code?: unknown };
    };
    response?: {
      _data?: {
        data?: { code?: unknown };
      };
    };
  } | null;
  const code =
    candidate?.data?.data?.code ??
    candidate?.response?._data?.data?.code ??
    candidate?.data?.code;

  if (code === "SHOPIFY_IMAGE_PROPAGATION") return CART_IMAGE_PROCESSING_MESSAGE;
  if (code === "SHOPIFY_IMAGE_REJECTED") return CART_IMAGE_REJECTED_MESSAGE;
  return DEFAULT_CART_ERROR_MESSAGE;
}

// Minimal structural shape of the store slice this helper touches.
// Using a local interface (not the full `useProjectStore` return type) keeps
// the composable usable without circular imports.
export interface ProjectStoreActionContext {
  designId: string | null;
  designName: string | null;
  isCommercialProject: boolean;
  projectName: string | null;
  salesperson: string | null;
  canFeature: boolean;
  isAddingToCart: boolean;
  getCanvasImage(): Promise<string>;
  buildDesignUpdate(): Record<string, unknown>;
  addToCart(
    options?: AddToCartOptions,
  ): Promise<{ success: boolean; error?: unknown }>;
}

export async function performAddToCart(
  store: ProjectStoreActionContext,
  {
    bypassLogin = false,
    bypassDesignName = false,
    bypassSaveDesign = false,
    attachmentOption = null as "adhesive" | "screws" | null,
    forceLegacyFlow = false,
  }: AddToCartOptions = {},
): Promise<{ success: boolean; error?: unknown }> {
  const userStore = useUserStore();
  const gridStore = useGridStore();
  const modalStore = useModalStore();
  const loadingStore = useLoadingStore();
  const { $swal } = useNuxtApp();
  const isMobileViewport = useState<boolean>("studio-is-mobile-viewport");
  const { shapeList } = storeToRefs(gridStore);

  // Check if 3D design - need to enable depth preview before capturing image
  const is3DDesign = gridStore.gridPattern?.id === GRID_PATTERN_IDS.THREE_D;
  const wasDepthPreviewEnabled = gridStore.showDepthPreview;

  // For 3D designs, enable depth preview before capturing
  if (is3DDesign && !gridStore.showDepthPreview) {
    gridStore.setShowDepthPreview(true);
    // Wait for next tick to allow canvas to re-render with depth
    await nextTick();
  }

  const imagePreviewPromise = store.getCanvasImage();

  try {
    store.isAddingToCart = true;

    // Convert Proxy array to regular array
    const tiles = Array.from(shapeList.value).filter(
      (shape: ShapeInterface, index: number, self: Array<ShapeInterface>) =>
        index === self.findIndex((s) => s.id === shape.id),
    );

    if (tiles.length <= 0) {
      await $swal.fire({
        icon: "error",
        text: "You must add at least 1 tile",
      });
      if (is3DDesign && !wasDepthPreviewEnabled) {
        gridStore.setShowDepthPreview(false);
      }
      return { success: false };
    }

    if (!userStore.isLoggedIn && !bypassLogin) {
      modalStore.toggleModal(ModalType.Login, {
        callback: () => store.addToCart({ attachmentOption, forceLegacyFlow }),
        options: {
          continueWithoutSaving: true,
          onContinueWithoutSaving: () =>
            store.addToCart({
              bypassLogin: true,
              bypassDesignName: true,
              bypassSaveDesign: true,
              attachmentOption,
              forceLegacyFlow,
            }),
        },
      });
      return { success: false };
    }

    // User is logged in but design hasn't been saved/named yet — show "Save Your Design?" modal
    if ((!store.designId || !store.designName) && !bypassSaveDesign && !bypassDesignName) {
      modalStore.toggleModal(ModalType.SaveDesign, {
        callback: () => store.addToCart({ bypassSaveDesign: true, bypassDesignName: true, attachmentOption, forceLegacyFlow }),
        options: {
          onContinueWithoutSaving: () =>
            store.addToCart({
              bypassSaveDesign: true,
              bypassDesignName: true,
              attachmentOption,
              forceLegacyFlow,
            }),
        },
      });
      return { success: false };
    }

    if (!store.designName && !bypassDesignName) {
      modalStore.toggleModal(ModalType.SaveAs, {
        callback: () => store.addToCart({ attachmentOption, forceLegacyFlow }),
        options: {
          continueWithoutSaving: true,
          save: true,
          keepLoading: true,
          onContinueWithoutSaving: () =>
            store.addToCart({
              bypassDesignName: true,
              attachmentOption,
              forceLegacyFlow,
            }),
        },
      });
      return { success: false };
    }

    loadingStore.setLoading(true);
    // Prepare cart tiles data
    const cartTiles = buildStudioCartTiles(tiles, shapeId => is3DDesign ? getShapeById(shapeId)?.quantity || 1 : 1);

    const imagePreview = await imagePreviewPromise;

    // AR model generation disabled — now handled via admin AR Model Manager (/admin/ar-models)
    const arModelBase64: string | null = null;

    // Create Shopify product
    const { POST } = await import("~/composables/fetchingTools");
    const saveUpdateData = store.buildDesignUpdate();
    const cartPayload = {
      ...saveUpdateData,
      designId: store.designId,
      designName: store.designName || "New",
      studioOrigin: typeof isMobileViewport.value === "boolean"
        ? isMobileViewport.value ? "mobile" : "desktop"
        : undefined,
      cartTiles,
      cost: Object.values(cartTiles).reduce(
        (acc: number, tile) => acc + tile.cost,
        0,
      ),
      isCommercialProject: store.isCommercialProject,
      projectName: store.projectName,
      canFeature: store.canFeature,
      image: imagePreview,
      gridProperties: {
        width: gridStore.gridWidth,
        height: gridStore.gridHeight,
        backgroundColor: gridStore.gridBgColor,
      },
      ...(arModelBase64 && { arModelBase64 }),
      ...(attachmentOption && { attachmentOption }),
      ...(forceLegacyFlow && { forceLegacyFlow: true }),
    };

    const response = await POST("/api/cart?parallel=true", cartPayload);

    if (!response.success) {
      await $swal.fire({
        icon: "error",
        text: DEFAULT_CART_ERROR_MESSAGE,
      });
      loadingStore.setLoading(false);
      throw new Error("Failed to create Shopify product");
    }

    // Preferred: POST cart-add payload via a hidden form. The fetch/form
    // encoder serializes values exactly once, so plaintext properties
    // (_custom_title, _preview_image) arrive at Shopify clean — unlike
    // the GET URL, which suffers paste-redirect double-encoding. The
    // `_sealed` blob still travels as a property for Cart Transform to
    // authorize price/tiles/weight at checkout.
    if (response.cartAddPayload) {
      submitCartAddViaForm(response.cartAddPayload, {
        isCommercialProject: store.isCommercialProject,
        salesperson: store.salesperson,
        projectName: store.projectName,
      });
      // form.submit() navigates top-level; spinner stays up until unload.
      return { success: true };
    }

    // Legacy GET-URL fallback for responses without `cartAddPayload`
    // (pre-migration servers). Subject to the URL double-encoding bug.
    if (response.url) {
      let targetUrl = response.url;

      if (store.isCommercialProject) {
        targetUrl += `&salesperson=${store.salesperson}&commercial_project=${store.isCommercialProject}&project_name=${store.projectName}`;
      }

      window.location.href = targetUrl;
      loadingStore.setLoading(false);
      return { success: true };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in addToCart:", error);
    loadingStore.setLoading(false);
    // Restore depth preview state on error
    if (is3DDesign && !wasDepthPreviewEnabled) {
      gridStore.setShowDepthPreview(false);
    }
    await $swal.fire({
      icon: "error",
      text: getAddToCartErrorMessage(error),
    });
    loadingStore.setLoading(false);
    return { success: false, error };
  } finally {
    store.isAddingToCart = false;
  }
}
